import { createRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { ScheduleTabs } from "../components/ScheduleTabs";
import { useBackupSchedule, type BackupAppointment } from "../queries/useBackupSchedule";
import { rootRoute } from "./__root";
import { useAuth } from "../auth/AuthContext";

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: "/schedule/backup",
  component: BackupScheduleScreen,
});

const dayFormat = new Intl.DateTimeFormat("en-US", { timeZone: "UTC", month: "short", day: "numeric", weekday: "short" });

export function tenantDate(timezone: string, now = new Date()) {
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone: timezone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(now);
  return ["year", "month", "day"].map((key) => parts.find((p) => p.type === key)?.value).join("-");
}

function plusDays(date: string, count: number) {
  const next = new Date(`${date}T12:00:00Z`);
  next.setUTCDate(next.getUTCDate() + count);
  return next.toISOString().slice(0, 10);
}

function dateLabel(date: string) { return dayFormat.format(new Date(`${date}T12:00:00Z`)); }

function BackupScheduleScreen() {
  const { hasRole } = useAuth();
  const query = useBackupSchedule();
  const snapshot = query.data?.ready ? query.data : null;
  const timezone = snapshot?.timezone || "UTC";
  const timeFormat = new Intl.DateTimeFormat("en-US", { timeZone: timezone, hour: "numeric", minute: "2-digit" });
  const savedFormat = new Intl.DateTimeFormat("en-US", { timeZone: timezone, month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  const [date, setDate] = useState("");
  const [route, setRoute] = useState("all");
  const [mode, setMode] = useState<"day" | "week">("day");
  const [selected, setSelected] = useState<string | null>(null);
  const closeDetails = useCallback(() => setSelected(null), []);
  useEffect(() => {
    if (snapshot) setDate((current) => {
      const chosen = current || tenantDate(snapshot.timezone);
      return chosen < snapshot.coverage_start ? snapshot.coverage_start
        : chosen > snapshot.coverage_end ? snapshot.coverage_end : chosen;
    });
  }, [snapshot?.coverage_start, snapshot?.coverage_end, snapshot?.timezone]);
  const end = mode === "week" ? plusDays(date, 6) : date;
  const appointments = snapshot?.appointments.filter((row) => row.date >= date && row.date <= end
    && (route === "all" || row.route_id === Number(route))) || [];
  const active = snapshot?.appointments.find((row) => row.work_order_ref === selected);
  const dates = [...new Set(appointments.map((row) => row.date))].sort();

  if (!hasRole("owner", "gm", "sales")) return <p role="alert">This schedule is available to owners, managers and sales staff.</p>;

  return <section className="space-y-5 pb-6">
    <ScheduleTabs backup />
    <div className="flex flex-wrap items-start justify-between gap-3">
      <h1 className="text-2xl font-semibold text-slate-950">Backup Schedule</h1>
      <button type="button" disabled={query.isFetching} onClick={() => void query.refetch()}
        className="rounded-md border border-slate-300 px-3 py-2 text-sm font-medium disabled:opacity-50">
        {query.isFetching ? "Refreshing..." : "Refresh"}
      </button>
    </div>
    {query.isError && <p role="alert" className="border-l-4 border-red-500 bg-red-50 p-3 text-sm text-red-800">
      Backup schedule could not load.{snapshot ? " The last loaded copy is still shown." : " Please try again."}
    </p>}
    {query.isLoading ? <p role="status">Loading saved appointments...</p>
      : !snapshot && !query.isError ? <p role="status" className="border-l-4 border-amber-500 bg-amber-50 p-4 text-amber-950">
        {query.data?.ready === false && query.data.reason === "backup_route_set_mismatch"
          ? "Saved schedule cannot be verified because team assignments changed. Contact your manager."
          : "No verified schedule backup is available yet."}
      </p> : null}
    {snapshot && <>
      <div role="status" className={`border-l-4 px-4 py-3 text-sm ${snapshot.stale ? "border-amber-500 bg-amber-50 text-amber-950" : "border-emerald-600 bg-emerald-50 text-emerald-950"}`}>
        <p className="font-semibold">{snapshot.stale ? "Older backup. Appointment changes may be missing." : "Saved schedule"}</p>
        <p className="mt-1">Last saved {savedFormat.format(new Date(snapshot.captured_at))} ({timezone})</p>
        <p>Coverage {dateLabel(snapshot.coverage_start)} to {dateLabel(snapshot.coverage_end)}. Times in {timezone}.</p>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-sm font-medium">Date
          <input type="date" value={date} min={snapshot.coverage_start} max={snapshot.coverage_end} onChange={(e) => e.target.value && setDate(e.target.value)}
            className="h-10 min-w-0 rounded-md border border-slate-300 bg-white px-2" />
        </label>
        <label className="flex min-w-0 flex-col gap-1 text-sm font-medium">Team member
          <select value={route} onChange={(e) => setRoute(e.target.value)} className="h-10 max-w-full rounded-md border border-slate-300 bg-white px-3">
            <option value="all">All</option>
            {snapshot.routes.map((r) => <option key={r.route_id} value={r.route_id}>{r.name}</option>)}
          </select>
        </label>
        <div aria-label="Schedule range" className="flex h-10 overflow-hidden rounded-md border border-slate-300">
          {(["day", "week"] as const).map((value) => <button key={value} type="button" aria-pressed={mode === value}
            onClick={() => setMode(value)} className={`px-4 text-sm font-medium ${mode === value ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`}>
            {value === "day" ? "Day" : "7 days"}
          </button>)}
        </div>
      </div>
      {appointments.length === 0 ? <p className="border-t border-slate-200 py-8 text-slate-600">No saved appointments for this selection.</p>
        : <div className="space-y-6">{dates.map((day) => <section key={day}>
          <h2 className="border-b border-slate-200 pb-2 text-base font-semibold">{dateLabel(day)}</h2>
          <div className="divide-y divide-slate-200">{appointments.filter((row) => row.date === day)
            .sort((a, b) => a.scheduled_at.localeCompare(b.scheduled_at) || a.route_name.localeCompare(b.route_name))
            .map((row) => <button key={row.work_order_ref} type="button" onClick={() => setSelected(row.work_order_ref)}
              className="grid w-full min-w-0 gap-2 py-4 text-left hover:bg-slate-50 sm:grid-cols-[100px_120px_1fr]">
              <span className="text-sm font-semibold text-slate-950">{timeFormat.format(new Date(row.scheduled_at))}
                {row.ends_at && <span className="block text-xs font-normal text-slate-600">to {timeFormat.format(new Date(row.ends_at))}</span>}
              </span>
              <span className="break-words text-sm font-medium text-blue-700">{row.route_name}</span>
              <span className="min-w-0 space-y-1">
                <span className="block break-words font-semibold">{row.customer_name || "Customer name unavailable"}</span>
                <span className="block break-words text-sm text-slate-600">{row.address || "Address unavailable"}</span>
                <span className="block text-xs text-slate-500">WO #{row.work_order_ref} {row.status ? ` / ${row.status}` : ""}</span>
              </span>
            </button>)}</div>
        </section>)}</div>}
    </>}
    {active && <AppointmentDetails appointment={active} timezone={timezone} onClose={closeDetails} />}
  </section>;
}

function AppointmentDetails({ appointment: row, timezone, onClose }: { appointment: BackupAppointment; timezone: string; onClose: () => void }) {
  const timeFormat = new Intl.DateTimeFormat("en-US", { timeZone: timezone, hour: "numeric", minute: "2-digit" });
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const close = document.getElementById("backup-detail-close");
    close?.focus();
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
      if (event.key === "Tab") {
        const controls = document.querySelectorAll<HTMLElement>("#backup-details a[href], #backup-details button");
        const first = controls[0]; const last = controls[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    };
    document.addEventListener("keydown", escape);
    return () => { document.removeEventListener("keydown", escape); previous?.focus(); };
  }, [onClose]);
  const phone = row.phone?.replace(/[^+0-9]/g, "");
  return <div className="fixed inset-0 z-[60] flex justify-end bg-black/30" onClick={onClose}>
    <div id="backup-details" role="dialog" aria-modal="true" aria-labelledby="backup-detail-title"
      onClick={(e) => e.stopPropagation()} className="h-full w-full max-w-xl overflow-y-auto bg-white p-5 shadow-xl sm:p-8">
      <div className="flex items-start justify-between gap-3">
        <h2 id="backup-detail-title" className="min-w-0 break-words text-xl font-semibold">{row.customer_name || "Saved appointment"}</h2>
        <button id="backup-detail-close" type="button" onClick={onClose} className="shrink-0 rounded-md border border-slate-300 px-3 py-2 text-sm">Close</button>
      </div>
      <p className="mt-2 text-sm text-slate-600">{dateLabel(row.date)} at {timeFormat.format(new Date(row.scheduled_at))} ({timezone}) / {row.route_name}</p>
      <dl className="mt-6 space-y-5 text-sm">
        <div><dt className="font-semibold">Work order</dt><dd className="mt-1">#{row.work_order_ref} {row.status}</dd></div>
        <div><dt className="font-semibold">Phone</dt><dd className="mt-1">{phone ? <a className="text-blue-700 underline" href={`tel:${phone}`}>{row.phone}</a> : "Not present in backup"}</dd></div>
        <div><dt className="font-semibold">Email</dt><dd className="mt-1 break-words">{row.email || "Not present in backup"}</dd></div>
        <div><dt className="font-semibold">Service address</dt><dd className="mt-1 break-words">{row.address ? <a className="text-blue-700 underline" target="_blank" rel="noopener noreferrer"
          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(row.address)}`}>{row.address}</a> : "Not present in backup"}</dd></div>
        <div><dt className="font-semibold">Scope of work</dt><dd className="mt-1 whitespace-pre-wrap break-words">{row.scope || "Not present in backup"}</dd></div>
        <div><dt className="font-semibold">Quote</dt><dd className="mt-1 space-y-2">
          {row.quote ? <><p>#{row.quote.id}{row.quote.total != null ? ` / ${new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(row.quote.total)}` : ""} {row.quote.status}</p>
            <p className="whitespace-pre-wrap break-words">{row.quote.details || "Itemized quote not present in backup"}</p>
          </> : "No verified quote link in backup"}
        </dd></div>
      </dl>
    </div>
  </div>;
}
